/**
 * TodaysPriceSection.tsx
 * 오늘의 시세 섹션 - 하드코딩 샘플 데이터
 */

import React, { useState } from "react";
import { Box, Typography, Grid, Chip } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import AddDeviceDialog from "../AddDeviceDialog";

// 등급 색상
const GRADE_COLORS: Record<string, string> = {
    A: '#10b981',
    B: '#3b82f6',
    C: '#f59e0b',
    D: '#ef4444',
};

// 메인 기기 상세 데이터
const mainDevice = {
    model: '갤럭시 Z플립4 256GB',
    brand: '삼성',
    date: '출시일: 2022-08-23',
    image: '/assets/device_phones.png',
    grades: { A: 160000, B: 125000, C: 90000, D: 50000 },
};

// 우측 인기 시세 리스트
const popularPrices = [
    { model: '아이폰 13 프로 256GB', grade: 'D', price: 262000, image: '/assets/device_phones.png' },
    { model: '갤럭시 Z폴드7 256GB', grade: 'A', price: 1390000, image: '/assets/device_phones.png' },
    { model: '갤럭시 S21 256GB', grade: 'A', price: 155000, image: '/assets/device_phones.png' },
];

const formatPrice = (price: number) => price.toLocaleString('ko-KR');

const TodaysPriceSection: React.FC = () => {
    const [quoteOpen, setQuoteOpen] = useState(false);

    return (
        <>
        <Box sx={{ py: { xs: 2, md: 3 }, background: '#fff' }}>
            <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 0 } }}>
                {/* 섹션 헤더 */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#0f172a', fontSize: { xs: '1rem', md: '1.2rem' } }}>
                            오늘의 시세
                        </Typography>
                        <AddIcon
                            onClick={() => setQuoteOpen(true)}
                            sx={{ fontSize: 18, color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#3b82f6' } }}
                        />
                    </Box>
                </Box>

                {/* 콘텐츠 영역 */}
                <Grid container spacing={2}>
                    {/* 좌측: 메인 기기 상세 카드 */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 2,
                            p: { xs: 2, md: 2.5 },
                            height: '100%',
                        }}>
                            {/* 기기 정보 */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Box
                                    component="img"
                                    src={mainDevice.image}
                                    alt={mainDevice.model}
                                    sx={{ width: 60, height: 60, objectFit: 'contain' }}
                                />
                                <Box>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                        {mainDevice.brand}
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.05rem' }, color: '#0f172a' }}>
                                        {mainDevice.model}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                        {mainDevice.date}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2 }}>
                                <Grid container spacing={1.5}>
                                    {Object.entries(mainDevice.grades).map(([grade, price]) => (
                                        <Grid item xs={6} key={grade}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={grade}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: GRADE_COLORS[grade],
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                        height: 22,
                                                        minWidth: 28,
                                                    }}
                                                />
                                                <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', md: '0.95rem' }, color: '#0f172a' }}>
                                                    {formatPrice(price)} 원
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>

                    {/* 우측: 인기 시세 리스트 */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                            {popularPrices.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 2,
                                        p: { xs: 1.5, md: 2 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flex: 1,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            borderColor: '#3b82f6',
                                            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            component="img"
                                            src={item.image}
                                            alt={item.model}
                                            sx={{
                                                width: { xs: 36, md: 44 },
                                                height: { xs: 36, md: 44 },
                                                objectFit: 'contain',
                                                borderRadius: 1,
                                                bgcolor: '#f8fafc',
                                                p: 0.5,
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={item.grade}
                                                size="small"
                                                sx={{
                                                    bgcolor: GRADE_COLORS[item.grade],
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '0.65rem',
                                                    height: 20,
                                                    minWidth: 24,
                                                }}
                                            />
                                            <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', md: '0.88rem' }, color: '#0f172a' }}>
                                                {item.model}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', md: '0.95rem' }, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                        {formatPrice(item.price)} 원
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
        <AddDeviceDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} mode="quote" />
        </>
    );
};

export default TodaysPriceSection;
